import { useCallback, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AddToListDialog } from '../components/AddToListDialog';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import {
  SOURCING_COMPANY_LISTS_KEY,
  SOURCING_LIST_COMPANIES_KEY,
  useBulkAddListItemsMutation,
  useSourcingListsQuery,
} from '../../../services/queries-hooks/SourcingListsQueriesHooks';
import { addCompaniesToList } from '../../../services/queries/SourcingListsQueries';
import { IAddCompanyToListRequest } from '../../../schemas/SourcingListItems.schema';
import { useCreateSourcingList } from '../hooks/useSourcingListActions';
import { useSourcingCompanyLists } from '../hooks/useSourcingCompanyLists';

interface AddToListFromProfileActionProps {
  /** Route segment (`:companyId`) — used as the React Query key for the
   *  profile-membership lookup so it stays in sync with the rest of the page. */
  readonly companyId: string;
  readonly name: string;
  /** Homepage URL or bare domain coming from the merged profile.
   *  Normalized to a bare domain before being sent to the API. */
  readonly website: string | null;
  readonly linkedinUrl: string | null;
  readonly sector: readonly string[];
  readonly industry: readonly string[];
}

/**
 * Self-contained "Add to List" action used by the sourcing profile header.
 *
 * Owns the modal open/close state and orchestrates the three queries that
 * back the flow:
 *   1. `useSourcingListsQuery` — every list the user owns (existing).
 *   2. `useSourcingCompanyLists(companyId)` — lists already containing this
 *      company; used to filter the picker so we never offer a duplicate.
 *   3. `useBulkAddListItemsMutation` — single-call write when the company
 *      has a stable domain (the common case from a profile page).
 *
 * If the profile has no domain we fall back to a parallel
 * `addCompaniesToList` per selected list using the LinkedIn URL — the
 * `bulk-add-items` endpoint matches company lists by domain only, so a
 * LinkedIn-only company would silently no-op there.
 */
export function AddToListFromProfileAction({
  companyId,
  name,
  website,
  linkedinUrl,
  sector,
  industry,
}: AddToListFromProfileActionProps) {
  const [open, setOpen] = useState(false);

  const { data: sourcingData } = useSourcingListsQuery();
  const { data: companyListsData } = useSourcingCompanyLists(companyId);

  const { pushErrorToast } = useToastMessageState();
  const bulkAddMutation = useBulkAddListItemsMutation();
  const createListMutation = useCreateSourcingList();
  const fallbackAddMutation = useFallbackAddCompanyToLists();

  const domain = useMemo(() => normalizeWebsiteToDomain(website), [website]);

  const alreadySavedListIds = useMemo(
    () => new Set((companyListsData?.lists ?? []).map((l) => l.listId)),
    [companyListsData]
  );

  // Hide already-saved lists so the user can't pick a duplicate. Person
  // lists are filtered out for the same reason — this action only ever
  // adds companies.
  const compatibleLists = useMemo(
    () =>
      (sourcingData?.lists ?? []).filter((l) => l.entityType === 'company' && !alreadySavedListIds.has(l.id)),
    [sourcingData, alreadySavedListIds]
  );

  const isLoading =
    bulkAddMutation.isPending || createListMutation.isPending || fallbackAddMutation.isPending;

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleConfirm = useCallback(
    async (listIds: string[], newListName?: string) => {
      // Reject the action up-front when neither identifier is usable rather
      // than firing a no-op request that the server would silently ignore.
      if (!domain && !linkedinUrl) {
        pushErrorToast({
          message: 'This company has no domain or LinkedIn URL — cannot add to a list.',
        });
        return;
      }

      const targetListIds = [...listIds];
      try {
        if (newListName) {
          const newList = await createListMutation.mutateAsync({
            name: newListName,
            entityType: 'company',
          });
          targetListIds.push(newList.id);
        }
        if (targetListIds.length === 0) {
          return;
        }

        if (domain) {
          // Primary path — single round trip via /lists/bulk-add-items.
          await bulkAddMutation.mutateAsync({ entityIds: [domain], listIds: targetListIds });
        } else {
          // Rare LinkedIn-only profile: bulk-add-items would silently no-op
          // for company lists, so we loop the per-list endpoint instead.
          const company: IAddCompanyToListRequest = {
            ...(name ? { name } : {}),
            ...(linkedinUrl ? { linkedinUrl } : {}),
            ...(sector[0] ? { sector: sector[0] } : {}),
            ...(industry[0] ? { industry: industry[0] } : {}),
          };
          await fallbackAddMutation.mutateAsync({ company, listIds: targetListIds });
        }

        setOpen(false);
      } catch {
        // Mutation hooks already toasted the error; keep the dialog open so
        // the user can adjust and retry.
      }
    },
    [
      bulkAddMutation,
      createListMutation,
      fallbackAddMutation,
      domain,
      linkedinUrl,
      name,
      sector,
      industry,
      pushErrorToast,
    ]
  );

  return (
    <>
      <Button
        variant='contained'
        color='secondary'
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onClick={handleOpen}
      >
        Add to List
      </Button>
      <AddToListDialog
        open={open}
        entityCount={1}
        entityType='company'
        lists={compatibleLists}
        isLoading={isLoading}
        onConfirm={handleConfirm}
        onClose={handleClose}
      />
    </>
  );
}

/**
 * Strips protocol / `www.` / path / query / trailing slash from a stored
 * website URL and returns the bare host. Returns `null` when the input is
 * missing or unparseable so callers can decide on a fallback path.
 *
 * The backend canonicalizes domains for us, but `bulk-add-items` matches
 * `entityIds` against `ListCompany.domain` exactly — sending a full URL
 * would not match. Mirrors the rules in `resolveCompanyLookup` while also
 * dropping path/query for resilience.
 */
export function normalizeWebsiteToDomain(website: string | null): string | null {
  if (!website) {
    return null;
  }
  const trimmed = website.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(withProtocol);
    const host = url.hostname.replace(/^www\./i, '').toLowerCase();
    return host.length > 0 ? host : null;
  } catch {
    // Hand-rolled fallback for inputs the URL constructor rejects (rare
    // because we always prepend a protocol above, but kept for safety).
    return (
      trimmed
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .replace(/[/?#].*$/, '')
        .toLowerCase()
        .trim() || null
    );
  }
}

/**
 * Mutation that adds a single company to N lists by calling
 * `addCompaniesToList` in parallel. Used only when we don't have a
 * usable domain — the primary path is `bulkAddListItems`. Kept private
 * to this file because it has no other consumers.
 */
function useFallbackAddCompanyToLists() {
  const queryClient = useQueryClient();
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: async ({ company, listIds }: { company: IAddCompanyToListRequest; listIds: string[] }) => {
      await Promise.all(listIds.map((listId) => addCompaniesToList(listId, [company])));
    },
    onSuccess: (_data, { listIds }) => {
      listIds.forEach((id) => {
        queryClient.invalidateQueries({ queryKey: [SOURCING_LIST_COMPANIES_KEY, id] });
      });
      queryClient.invalidateQueries({ queryKey: [SOURCING_COMPANY_LISTS_KEY] });
      const listLabel = listIds.length === 1 ? '1 list' : `${listIds.length} lists`;
      pushSuccessToast({ message: `Added to ${listLabel}` });
    },
    onError: () => {
      pushErrorToast({ message: 'Failed to add to lists. Please try again.' });
    },
  });
}
